import {
    AspectRatio,
    Box,
    Button,
    Card,
    CardContent,
    CardOverflow,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Input,
    Modal,
    ModalClose,
    ModalDialog,
    DialogTitle,
    DialogContent,
    Sheet,
    Stack,
    Tooltip,
    Typography,
} from "@mui/joy";
import {
    AudioFile,
    CheckCircle,
    Close,
    ContentCopy,
    Delete,
    Description,
    GridView,
    InsertDriveFile,
    Inventory2,
    PictureAsPdf,
    Search,
    Upload,
    VideoFile,
    ViewList,
} from "@mui/icons-material";
import { PageStandard } from "../utils/PageStandard";
import { useContext, useEffect, useRef, useState } from "react";
import { deleteFile, listFiles, uploadFile } from "../utils/ViewModel";
import { formatAsLink } from "../utils/Utils";
import { FeedbackContext } from "../utils/FeedbackContext";

// ── helpers ───────────────────────────────────────────────────────────────────

const BASE_URL = "https://api.gocicero.it/media/getFile/";

type FileType = "image" | "video" | "audio" | "pdf" | "other";

function getFileType(name: string): FileType {
    const ext = name.split(".").pop()?.toLowerCase() ?? "";
    if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "avif"].includes(ext)) return "image";
    if (["mp4", "mov", "avi", "webm", "mkv"].includes(ext)) return "video";
    if (["mp3", "wav", "ogg", "m4a", "aac"].includes(ext)) return "audio";
    if (ext === "pdf") return "pdf";
    return "other";
}

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function FileTypeIcon({ type, size = 32 }: { type: FileType; size?: number }) {
    const sx = { fontSize: size, color: "var(--joy-palette-neutral-400)" };
    switch (type) {
        case "video": return <VideoFile sx={{ ...sx, color: "var(--joy-palette-primary-400)" }} />;
        case "audio": return <AudioFile sx={{ ...sx, color: "var(--joy-palette-success-400)" }} />;
        case "pdf": return <PictureAsPdf sx={{ ...sx, color: "var(--joy-palette-danger-400)" }} />;
        default: return <InsertDriveFile sx={sx} />;
    }
}

function fileTypeColor(type: FileType): "primary" | "success" | "danger" | "neutral" {
    switch (type) {
        case "image": return "neutral";
        case "video": return "primary";
        case "audio": return "success";
        case "pdf": return "danger";
        default: return "neutral";
    }
}

// ── main page ─────────────────────────────────────────────────────────────────

export function Storage() {
    const { setMessage, setError } = useContext(FeedbackContext);

    const [files, setFiles] = useState<string[]>([]);
    const [loadingFiles, setLoadingFiles] = useState(true);
    const [search, setSearch] = useState("");
    const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
    const [uploadOpen, setUploadOpen] = useState(false);

    const refresh = async () => {
        const result = await listFiles();
        setFiles(result);
    };

    useEffect(() => {
        refresh().finally(() => setLoadingFiles(false));
    }, []);

    const handleDeleted = (name: string) => {
        setFiles((prev) => prev.filter((f) => f !== name));
        setMessage(`"${name}" eliminato`);
    };

    const handleDeleteError = (name: string) => {
        setError(`Errore durante l'eliminazione di "${name}"`);
    };

    const handleCopied = () => setMessage("URL copiato negli appunti");

    const filtered = files.filter((f) =>
        f.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <PageStandard
            title="Storage"
            sidebarVisible={true}
            headerEnd={
                <Button startDecorator={<Upload />} onClick={() => setUploadOpen(true)}>
                    Carica file
                </Button>
            }
        >
            {/* Toolbar */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5 }}>
                <Input
                    placeholder="Cerca file…"
                    startDecorator={<Search />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: 1, maxWidth: 380 }}
                    endDecorator={
                        search ? (
                            <IconButton size="sm" variant="plain" onClick={() => setSearch("")}>
                                <Close />
                            </IconButton>
                        ) : null
                    }
                />
                <Typography level="body-sm" color="neutral" sx={{ whiteSpace: "nowrap" }}>
                    {filtered.length} / {files.length} file
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                        size="sm"
                        variant={viewMode === "grid" ? "soft" : "plain"}
                        onClick={() => setViewMode("grid")}
                    >
                        <GridView />
                    </IconButton>
                    <IconButton
                        size="sm"
                        variant={viewMode === "list" ? "soft" : "plain"}
                        onClick={() => setViewMode("list")}
                    >
                        <ViewList />
                    </IconButton>
                </Box>
            </Stack>

            {/* Content */}
            {loadingFiles ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : filtered.length === 0 ? (
                <EmptyState hasSearch={search.length > 0} />
            ) : viewMode === "grid" ? (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                        gap: 2,
                    }}
                >
                    {filtered.map((f) => (
                        <GridFileCard
                            key={f}
                            fileName={f}
                            onDeleted={handleDeleted}
                            onDeleteError={handleDeleteError}
                            onCopied={handleCopied}
                        />
                    ))}
                </Box>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: "md", overflow: "hidden" }}>
                    {filtered.map((f, i) => (
                        <ListFileRow
                            key={f}
                            fileName={f}
                            divider={i < filtered.length - 1}
                            onDeleted={handleDeleted}
                            onDeleteError={handleDeleteError}
                            onCopied={handleCopied}
                        />
                    ))}
                </Sheet>
            )}

            {/* Upload modal */}
            <UploadModal
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onUploaded={() => { refresh(); setMessage("File caricati con successo"); }}
                onError={(msg) => setError(msg)}
            />
        </PageStandard>
    );
}

// ── empty state ───────────────────────────────────────────────────────────────

function EmptyState({ hasSearch }: { hasSearch: boolean }) {
    return (
        <Box sx={{ textAlign: "center", py: 10 }}>
            <Inventory2 sx={{ fontSize: 56, color: "var(--joy-palette-neutral-300)", mb: 1 }} />
            <Typography level="title-md" color="neutral">
                {hasSearch ? "Nessun file trovato" : "Nessun file caricato"}
            </Typography>
            <Typography level="body-sm" color="neutral">
                {hasSearch ? "Prova con un termine diverso" : "Carica il primo file con il pulsante in alto"}
            </Typography>
        </Box>
    );
}

// ── grid card ─────────────────────────────────────────────────────────────────

interface FileCardProps {
    fileName: string;
    onDeleted: (name: string) => void;
    onDeleteError: (name: string) => void;
    onCopied: () => void;
}

function GridFileCard({ fileName, onDeleted, onDeleteError, onCopied }: FileCardProps) {
    const url = BASE_URL + formatAsLink(fileName);
    const type = getFileType(fileName);
    const [imgError, setImgError] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(url);
        onCopied();
    };

    const handleDelete = async () => {
        if (!window.confirm(`Eliminare definitivamente "${fileName}"?`)) return;
        setDeleting(true);
        const ok = await deleteFile(fileName);
        if (ok) onDeleted(fileName);
        else onDeleteError(fileName);
        setDeleting(false);
    };

    return (
        <Card variant="outlined" sx={{ gap: 0 }}>
            <CardOverflow>
                <AspectRatio ratio="1" sx={{ bgcolor: "background.level2" }}>
                    {type === "image" && !imgError ? (
                        <img
                            src={url}
                            loading="lazy"
                            alt={fileName}
                            onError={() => setImgError(true)}
                            style={{ objectFit: "cover" }}
                        />
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FileTypeIcon type={type} size={48} />
                        </Box>
                    )}
                </AspectRatio>
            </CardOverflow>

            <CardContent sx={{ p: 1, gap: 0.5 }}>
                <Tooltip title={fileName} placement="top">
                    <Typography
                        level="body-sm"
                        fontWeight="md"
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {fileName}
                    </Typography>
                </Tooltip>
                <Chip size="sm" variant="soft" color={fileTypeColor(type)}>
                    {type.toUpperCase()}
                </Chip>
            </CardContent>

            <Divider />
            <CardContent orientation="horizontal" sx={{ p: 0.5, justifyContent: "flex-end", gap: 0.5 }}>
                <Tooltip title="Copia URL">
                    <IconButton size="sm" variant="plain" color="neutral" onClick={handleCopy}>
                        <ContentCopy sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Elimina">
                    <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={handleDelete}
                        loading={deleting}
                    >
                        <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            </CardContent>
        </Card>
    );
}

// ── list row ──────────────────────────────────────────────────────────────────

interface ListRowProps extends FileCardProps {
    divider: boolean;
}

function ListFileRow({ fileName, divider, onDeleted, onDeleteError, onCopied }: ListRowProps) {
    const url = BASE_URL + formatAsLink(fileName);
    const type = getFileType(fileName);
    const [deleting, setDeleting] = useState(false);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(url);
        onCopied();
    };

    const handleDelete = async () => {
        if (!window.confirm(`Eliminare definitivamente "${fileName}"?`)) return;
        setDeleting(true);
        const ok = await deleteFile(fileName);
        if (ok) onDeleted(fileName);
        else onDeleteError(fileName);
        setDeleting(false);
    };

    return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ px: 2, py: 1.25 }}
            >
                <FileTypeIcon type={type} size={22} />
                <Typography level="body-sm" fontWeight="md" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {fileName}
                </Typography>
                <Chip size="sm" variant="soft" color={fileTypeColor(type)} sx={{ flexShrink: 0 }}>
                    {type}
                </Chip>
                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                    <Tooltip title="Copia URL">
                        <IconButton size="sm" variant="plain" color="neutral" onClick={handleCopy}>
                            <ContentCopy sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina">
                        <IconButton size="sm" variant="plain" color="danger" onClick={handleDelete} loading={deleting}>
                            <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>
            {divider && <Divider />}
        </>
    );
}

// ── upload modal ──────────────────────────────────────────────────────────────

interface UploadModalProps {
    open: boolean;
    onClose: () => void;
    onUploaded: () => void;
    onError: (msg: string) => void;
}

function UploadModal({ open, onClose, onUploaded, onError }: UploadModalProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState<{ name: string; done: boolean }[]>([]);

    const addFiles = (incoming: FileList | null) => {
        if (!incoming) return;
        const arr = Array.from(incoming);
        setFiles((prev) => {
            const existingNames = new Set(prev.map((f) => f.name));
            return [...prev, ...arr.filter((f) => !existingNames.has(f.name))];
        });
    };

    const removeFile = (name: string) => setFiles((prev) => prev.filter((f) => f.name !== name));

    const handleClose = () => {
        if (uploading) return;
        setFiles([]);
        setProgress([]);
        onClose();
    };

    const handleUpload = async () => {
        if (files.length === 0 || uploading) return;
        setUploading(true);
        setProgress(files.map((f) => ({ name: f.name, done: false })));

        let hasError = false;
        for (let i = 0; i < files.length; i++) {
            const result = await uploadFile(files[i]);
            if (!result) { hasError = true; }
            setProgress((prev) => prev.map((p, idx) => idx === i ? { ...p, done: true } : p));
        }

        setUploading(false);
        if (!hasError) {
            setFiles([]);
            setProgress([]);
            onUploaded();
            onClose();
        } else {
            onError("Alcuni file non sono stati caricati correttamente");
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <ModalDialog sx={{ width: "min(520px, 95vw)" }}>
                <ModalClose />
                <DialogTitle>Carica file</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        {/* Drop zone */}
                        <Box
                            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                border: `2px dashed`,
                                borderColor: dragging ? "primary.400" : "neutral.300",
                                borderRadius: "md",
                                p: 4,
                                textAlign: "center",
                                cursor: "pointer",
                                bgcolor: dragging ? "primary.50" : "background.level1",
                                transition: "all 0.15s",
                                "&:hover": { borderColor: "primary.400", bgcolor: "primary.50" },
                            }}
                        >
                            <Upload sx={{ fontSize: 36, color: "neutral.400", mb: 0.5 }} />
                            <Typography level="body-sm" fontWeight="md">
                                Trascina i file qui o <Typography color="primary">clicca per scegliere</Typography>
                            </Typography>
                            <Typography level="body-xs" color="neutral">
                                Immagini, video, audio, PDF e altri formati
                            </Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                style={{ display: "none" }}
                                onChange={(e) => addFiles(e.target.files)}
                            />
                        </Box>

                        {/* Selected files list */}
                        {files.length > 0 && (
                            <Stack spacing={0.5}>
                                <Typography level="body-xs" color="neutral" fontWeight="md">
                                    {files.length} file selezionati
                                </Typography>
                                <Sheet variant="outlined" sx={{ borderRadius: "sm", overflow: "hidden" }}>
                                    {files.map((f, i) => {
                                        const prog = progress[i];
                                        return (
                                            <Box key={f.name}>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1}
                                                    sx={{ px: 1.5, py: 1 }}
                                                >
                                                    <Description sx={{ fontSize: 18, color: "neutral.400", flexShrink: 0 }} />
                                                    <Stack sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            level="body-xs"
                                                            fontWeight="md"
                                                            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                        >
                                                            {f.name}
                                                        </Typography>
                                                        <Typography level="body-xs" color="neutral">
                                                            {formatSize(f.size)}
                                                        </Typography>
                                                    </Stack>
                                                    {prog ? (
                                                        prog.done
                                                            ? <CheckCircle sx={{ fontSize: 18, color: "var(--joy-palette-success-400)" }} />
                                                            : <CircularProgress size="sm" sx={{ "--CircularProgress-size": "18px" }} />
                                                    ) : (
                                                        <IconButton
                                                            size="sm"
                                                            variant="plain"
                                                            color="neutral"
                                                            onClick={() => removeFile(f.name)}
                                                        >
                                                            <Close sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                                {i < files.length - 1 && <Divider />}
                                            </Box>
                                        );
                                    })}
                                </Sheet>
                            </Stack>
                        )}

                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button variant="plain" color="neutral" onClick={handleClose} disabled={uploading}>
                                Annulla
                            </Button>
                            <Button
                                onClick={handleUpload}
                                disabled={files.length === 0 || uploading}
                                loading={uploading}
                                startDecorator={<Upload />}
                            >
                                Carica {files.length > 0 ? `${files.length} file` : ""}
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
            </ModalDialog>
        </Modal>
    );
}
